// DECK WAYS (owner 2026-10-16: "the train tracks should look the same on the
// bridge as off the bridge, same for roads"). Off a deck a way is ground: the
// bake's ways field + client/src/groundmat.js draw its layer (hex-tiled scan),
// the painter's mean albedo (uWayAlb), the kerb line, the lane lines and the
// rail sleepers, lit by the bake's one light model. On a deck the top is a
// structure triangle (structs2: flat MAT_DRAW colour) — so every deck stretch
// whose top is not timber gets its way's OWN look drawn on the deck top:
//   deckWays     the pieces: deckRibbons' shape (game/waysdraw.js; its ribbon
//                on the deck top y + cf o, clipped to the top's edges eL / eR,
//                joined to the drawn approach sections either side) for EVERY
//                way kind, not only roads on tarmac. Skipped: a timber top
//                (owner: an unpaved trail's footbridge stays planks — a paved
//                footway's deck shows its way), a stairs deck (its STAIR
//                nosings, emit.mat.stair), the funicular (kind none: W's open
//                steel track is its look on and off the deck, AMENDMENT 1 A4).
//   deckWayMaterial  (client/src/deckwaymat.js: three + the ground shader; this
//                file stays pure — decode.js and the node gates import it)
//                the ground's way code driven per vertex instead of by the
//                field: aDw = (o across, s along = the run's arc, the look byte
//                kind | hi << 4, the run width); the paint parts / lane marks
//                as the old deck ribbons (aPart / aLane / aCut). Same layers +
//                hex tiling (world-anchored uv: the scan runs on across the deck
//                end), same albedo, kerb / lane-line / sleeper patterns at the
//                pixel footprint (gmCov / gmKeep: the far fade), the same near
//                detail / relight fades. Light = the bake's model (meta.tex.light
//                sky / ground / sun, gmE) at sky and sun visibility 1 — a baked
//                shadow is not reproduced on a deck (accepted) — then three's
//                tone mapping: the one `look` curve, as the terrain.
//   deckRails    the rail head lines over the deck stations (structplan railRuns
//                { deck: 'only' }: its plain pairs at the run's gauge, a deck
//                turnout's single lines), drawn by ways2 as the ground's railUnit
//                instances; the deck pieces' 3D sleepers come from nearDetail on
//                the same phase as the ground (s = k x pitch of the run's arc).
import { renderOf, wayKind, railPoint } from '../../game/waysdraw.js';
import WAYSLOOK from '../../pipeline/wayslook.json' with { type: 'json' };

// lift_m / match_m = game/waysdraw.js DECK_RIBBON's (the ribbon over the top,
// a deck station's match radius); every deck names its top_mat (world/emit.js
// deckTop) — a deck without one is a broken bake and throws
export const DECK_WAY = { lift_m: 0.01, match_m: 0.05, skip_top: 'timber' };
const KN = WAYSLOOK.kinds;
export const PATH_KINDS = ['soil', 'rock', 'track'].map((n) => KN[n]);

// deckWays(doc, drawn) -> {pieces (piece-shaped, deck: 1, look: the look
// byte), stats}; decodeWays runs paintSteps / laneSteps over them as before
export function deckWays(doc, drawn) {
  const runs = doc.ways, F = drawn.frames;
  const K = (doc.codes && doc.codes.kind) || ['surface', 'deck', 'bore', 'drop'];
  const BORE = K.indexOf('bore'), DROP = K.indexOf('drop');
  const stats = { pieces: 0, sections: 0, joined: 0, unmatched: 0, lone: 0, deck_m: 0, skipped_decks: 0, by_kind: {} };
  const pieces = [];
  if (!Array.isArray(doc.decks) || !doc.decks.length) return { pieces, stats };
  const names = Object.keys(KN);
  // every deck station of a deck that shows its way, by position (1 m cells)
  const cell = new Map();
  doc.decks.forEach((d, k) => {
    if (!Array.isArray(d.x)) return;
    if (d.top_mat === undefined) throw new Error(`world ways.json decks[${k}].top_mat missing: rebuild this world (Build)`);
    if (d.stairs || d.cls === 'funicular' || d.top_mat === DECK_WAY.skip_top) { stats.skipped_decks++; return; }
    for (let j = 0; j < d.x.length; j++) {
      const kk = Math.floor(d.x[j]) + ':' + Math.floor(d.z[j]);
      let a = cell.get(kk);
      if (!a) cell.set(kk, a = []);
      a.push(k, j);
    }
  });
  const find = (x, z) => {
    let best = null, bd = DECK_WAY.match_m;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const a = cell.get((Math.floor(x) + dx) + ':' + (Math.floor(z) + dz));
        if (!a) continue;
        for (let t = 0; t < a.length; t += 2) {
          const d = doc.decks[a[t]], j = a[t + 1], dd = Math.hypot(d.x[j] - x, d.z[j] - z);
          if (dd <= bd) { bd = dd; best = [d, j]; }
        }
      }
    }
    return best;
  };
  // the drawn sections at a piece end, by run and station
  const ends = new Map();
  for (const p of drawn.pieces) {
    for (const c of [p.sec[0], p.sec[p.sec.length - 1]]) if (!c.cap) ends.set(p.run + ':' + c.i, c);
  }
  runs.forEach((e, r) => {
    if (!e || !Array.isArray(e.kind) || e.x.length < 2 || e.cls === 'funicular' || e.cls === 'stairs' || e.pcls === 'steps') return;
    const wk = wayKind(e, WAYSLOOK);
    if (!wk.kind) return;
    // a PATH-G kind's hi nibble is its sac / grade (its cross profile), a built
    // kind's its side strip's kind (sidewalk)
    const look = wk.kind | ((PATH_KINDS.includes(wk.kind) ? wk.hi : wk.side) & 15) << 4;
    const kname = names.find((n) => KN[n] === wk.kind);
    const n = e.x.length, { nx, nz } = F[r], h = e.w / 2, RD = renderOf(e);
    const s = new Float64Array(n);
    for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(e.x[i] - e.x[i - 1], e.z[i] - e.z[i - 1]);
    const deckSec = (i) => {
      const m = find(e.x[i], e.z[i]);
      if (!m) return null;
      const [d, j] = m;
      const sg = nx[i] * d.nx[j] + nz[i] * d.nz[j] < 0 ? -1 : 1;
      // the run's offsets o on the deck's own axis: o_deck = sg o
      const lo = Math.max(-h, sg > 0 ? -d.eL[j] : -d.eR[j]), hi = Math.min(h, sg > 0 ? d.eR[j] : d.eL[j]);
      if (!(hi - lo >= 0.5)) return null;
      const v = (o) => [e.x[i] + nx[i] * o, d.top[j] + d.cf[j] * sg * o + DECK_WAY.lift_m, e.z[i] + nz[i] * o];
      return { i, s: s[i], lo, hi, a: v(lo), b: v(hi), cap: 0 };
    };
    let cur = null;
    const flush = (endI) => {
      if (cur && cur.length) {
        const c = endI !== undefined ? ends.get(r + ':' + endI) : null;
        if (c) { cur.push(c); stats.joined++; }
        if (cur.length >= 2) {
          pieces.push({ run: r, cls: e.cls, mat: RD.mat, view: RD.view, texLen: RD.texLen, rails: 0, w: e.w, deck: 1, look, sec: cur });
          const m = cur[cur.length - 1].s - cur[0].s;
          stats.pieces++;
          stats.sections += cur.length;
          stats.deck_m += m;
          stats.by_kind[kname] = (stats.by_kind[kname] || 0) + m;
        } else stats.lone++;    // one deck station, no approach drawn either side
      }
      cur = null;
    };
    for (let i = 0; i < n; i++) {
      // a deck station: a structure station neither bore nor drop (kind deck,
      // or a declared span: kind surface + flag)
      const isDeck = e.flag[i] !== 0 && e.kind[i] !== BORE && e.kind[i] !== DROP;
      if (!isDeck) { flush(i); continue; }
      const c = deckSec(i);
      if (!c) { stats.unmatched++; flush(); continue; }
      if (!cur) {
        cur = [];
        const a = i > 0 ? ends.get(r + ':' + (i - 1)) : null;
        if (a) { cur.push(a); stats.joined++; }
      }
      cur.push(c);
    }
    flush();
  });
  stats.deck_m = Math.round(stats.deck_m);
  for (const k of Object.keys(stats.by_kind)) stats.by_kind[k] = Math.round(stats.by_kind[k]);
  return { pieces, stats };
}

// the rail head lines over the deck stations, ways2's head-line shape
// [{run, pts: [[x, y (the bed: ways2 adds RAIL_LIFT), z, nx, nz]...]}]: a plain
// pair (gauge > 0, run stations i0..i1) as two lines at +-gauge / 2 on the
// run's own frames (forkDraw's railPoint: a deck end meets the ground rail at
// its station exactly); a single line (a deck turnout's blade / outer / frog,
// gauge 0) as drawn, its rail_lift taken off. RR = structplan railRuns(doc,
// { deck: 'only' }); frames = drawn.frames (waysdraw runFrames)
export function deckRails(doc, RR, frames, lift) {
  const out = [];
  for (const E of RR) {
    if (E.gauge > 0) {
      const e = doc.ways[E.run], F = frames[E.run];
      if (!e || !F || !(E.i1 > E.i0)) continue;
      for (const sd of [-1, 1]) {
        const pts = [];
        for (let k = E.i0; k <= E.i1; k++) {
          const n = [F.nx[k], F.nz[k]], q = railPoint([e.x[k], e.y[k], e.z[k]], n, sd, E.gauge);
          pts.push([q[0], q[1], q[2], n[0], n[1]]);
        }
        out.push({ run: E.run, pts });
      }
    } else if (E.pts && E.pts.length >= 2) {
      out.push({ run: E.run, pts: E.pts.map((p) => [p[0], p[1] - lift, p[2], 0, 0]) });
    }
  }
  return out;
}

// the per-vertex deck way record of a section's two vertices (lo, hi)
export function deckWayAttr(out, p, c) {
  out.push(c.lo, c.s, p.look, p.w, c.hi, c.s, p.look, p.w);
}
